import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuthContext } from '../../../context/auth.context'
import { lmsAdmin } from '../api'
import { AgoCell, Clip, FAILED_STATE, LmsGrid, NameCell, PhoneLine, agoOf, byField, byText, connectionCell, exportTime, msOf, useGridRows } from '../lms-grid'
import { LmsIcon } from '../lms-icon'
import { Pill } from '../../../shared/sso-workspace/pill'
import { useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { useIsMobile } from '../../../shared/hooks/use-is-mobile'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../shared/table-builder/table-ag'
import { ColumnFormat } from '../../../shared/table-builder/table-columns'
import { IconSession } from '../../../shared/icon/icon'
import '../lms-admin.scss'
import { LmsTrail, lmsTrail } from '../lms-trail'
import { SECTION } from '../../../shared/navigation/sections'

// Active Sessions (docs/08 §12, design edxr-active-sessions-v1.0.html).
// Real-time-ish view of ready/claimed/in_progress launch tokens, polled
// every 10 seconds by the grid itself (pollInterval, with its ready as the
// loader). Rows link to Session Detail.

const POLL_MS = 10000

export const SESSION_STATE = {
  ready: { tone: 'info', label: 'Ready', rank: 0, sub: 'awaiting claim in the app' },
  claimed: { tone: 'warn', label: 'Claimed', rank: 1, sub: 'app opened, experience not started' },
  in_progress: { tone: 'ok', label: 'In progress', rank: 2, sub: 'experience running' }
}

// A session is named by its ref (the first 16 hex characters of its launch
// token's SHA-256): the token is a bearer credential the admin API never hands
// out (LMS final review M2). The cell shows the ref's start.
const shortRef = ref => `${(ref || '').slice(0, 10)}…`

// A session sitting unclaimed/unstarted this long is probably abandoned
// (the artifact's "stalled" variant).
const STALLED_MS = 15 * 60 * 1000
const isStalled = session =>
  session.state !== 'in_progress' && Date.now() - new Date(session.created_at).getTime() > STALLED_MS

export const ViewActiveSessions = () => {
  const history = useHistory()
  const authContext = useAuthContext()

  const [connections, updateConnections] = useState([])
  const [connectionFilter, updateConnectionFilter] = useState('')
  const tableRef = useRef()
  const mobile = useIsMobile()

  // The sessions (useGridRows): the grid's ready is the loader, and its poll
  // runs it again every POLL_MS (pollFails: a poll that fails throws, so the
  // grid's Live pill says Reconnecting rather than claiming it is current).
  // Choosing another connection reads again at once; the sessions already
  // shown stay on a failed poll, and a slow answer for a connection chosen
  // before is dropped.
  const { rows: sessions, error, ready } = useGridRows(
    connectionId => lmsAdmin.listActiveSessions({ connectionId }).then(result => result.sessions || []),
    { filter: connectionFilter, pollFails: true }
  )

  useEffect(() => {
    lmsAdmin
      .listConnections()
      .then(result => updateConnections(result.connections || []))
      .catch(() => {})
  }, [])

  const connectionsById = useMemo(() => {
    const map = {}
    connections.forEach(connection => {
      map[connection.id] = connection
    })
    return map
  }, [connections])

  const counts = useMemo(() => {
    const all = sessions || []
    return {
      ready: all.filter(s => s.state === 'ready').length,
      claimed: all.filter(s => s.state === 'claimed').length,
      inProgress: all.filter(s => s.state === 'in_progress').length
    }
  }, [sessions])

  // The cells' words, flattened onto the row: the grid sorts, quick-filters
  // and exports the cell VALUE, so each column's field holds what it shows.
  // The session's ref is the row's id, so a poll keeps the rows it already has.
  const gridRows = useMemo(
    () =>
      Array.isArray(sessions)
        ? sessions.map(session => {
          const connection = connectionsById[session.connection_id]
          const state = SESSION_STATE[session.state] || SESSION_STATE.ready
          const launchedAt = session.started_at || session.created_at
          const stalled = isStalled(session)
          return {
            ...session,
            id: session.ref,
            tokenText: shortRef(session.ref),
            stateText: state.label,
            stateTone: state.tone,
            stateRank: state.rank,
            courseName: session.course ? session.course.name : '—',
            learnerText: session.edxr_user_email || 'not yet linked',
            // the course, the learner and the ref in one value, so the grid's filter finds any of them
            courseText: `${session.course ? session.course.name : '—'} ${session.edxr_user_email || 'not yet linked'} ${session.ref}`,
            connectionName: connection ? connection.name : `connection ${session.connection_id}`,
            connectionPlatform: connection && connection.preset ? connection.preset.name : '',
            launchedAt,
            launchedMs: msOf(launchedAt),
            phone: mobile,
            launchedText: `${agoOf(launchedAt)}${stalled ? ' Stalled' : ''}`,
            stalled
          }
        })
        : sessions,
    [sessions, connectionsById, mobile]
  )
  const gridList = useGridList(gridRows)

  const customer = authContext.state.organizationName

  return (
    <div className="lms-admin" data-testid="lms-active-sessions">
      <LmsTrail trail={lmsTrail(SECTION.lmsSessions)} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">
            Active Sessions
            <span className="page-header-customer">
              <LmsIcon name="building" />
              {customer}
            </span>
          </h1>
          <p className="page-header-sub">
            Sessions currently in flight — launched from an LMS and not yet completed. Refreshes every 10 seconds.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {`Couldn't load sessions: ${error.message}`}
        </div>
      )}

      <div className="summary-row">
        <div className="summary-stat">
          <div className="stat-label">Ready</div>
          <div className="stat-value" data-testid="stat-ready">{counts.ready}</div>
          <div className="stat-sub">awaiting claim in the app</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">Claimed</div>
          <div className="stat-value">{counts.claimed}</div>
          <div className="stat-sub">app opened, not yet started</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">In progress</div>
          <div className="stat-value" data-testid="stat-in-progress">{counts.inProgress}</div>
          <div className="stat-sub">experience running now</div>
        </div>
      </div>

      <div className="filter-strip">
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={connectionFilter}
          data-testid="connection-filter"
          aria-label="Connection"
          onChange={e => updateConnectionFilter(e.target.value)}
        >
          <option value="">All connections</option>
          {connections.map(connection => (
            <option key={connection.id} value={connection.id}>
              {connection.name}
            </option>
          ))}
        </select>
        <div className="spacer" />
      </div>

      <LmsGrid title="live sessions" tableRef={tableRef} exportName="lms-sessions">
        <TableAgClient
          ref={tableRef}
          apiList={gridList}
          onReady={ready}
          pollInterval={POLL_MS}
          sortCol="launchedText"
          sortDesc
          rowHeight={ROW_HEIGHT_TWO_LINE}
          onRowClicked={session => history.push(`/org/lms/sessions/${session.ref}`)}
          emptyState={sessions === null ? FAILED_STATE : {
            Icon: IconSession,
            title: 'No live sessions right now',
            message: "When learners launch courses from the LMS, their sessions appear here while they're ready, claimed, or in progress. Completed sessions move to the Activity feed."
          }}
          noMatchState={{ message: 'No sessions match. Clear the search.' }}>
          <ColumnFormat
            field="courseText"
            header="Session"
            render={sessionCell}
            exportValue={exportSession}
            comparator={byText('courseName')}
            stacked
            minWidth={mobile ? 150 : 240}
            flex={2}
            sortable
          />
          <ColumnFormat field="stateText" header="State" render={stateCell} comparator={byField('stateRank')} sortable hide={mobile} />
          <ColumnFormat field="connectionName" header="Connection" render={connectionCell} stacked minWidth={170} flex={1} sortable hide={mobile} />
          <ColumnFormat
            field="launchedText"
            header="Launched"
            render={launchedCell}
            exportValue={exportLaunched}
            comparator={byField('launchedMs')}
            sortable
            hide={mobile}
          />
        </TableAgClient>
      </LmsGrid>
    </div>
  )
}

// The course (a link to the session's detail page) over the learner and the
// session's ref; on a phone the state, Stalled and when it launched, then the
// learner, in place of the columns a phone has no room for.
// The Session column's CSV value: the course, the learner and the session's
// ref (the whole ref, not the start the cell shows).
export const exportSession = session => `${session.courseName} · ${session.learnerText} · ${session.ref}`

// The Launched column's CSV value: the precise UTC time, and whether the session
// has stalled (the cell's pill).
export const exportLaunched = session => `${exportTime(session.launchedAt)}${session.stalled ? ' (stalled)' : ''}`

const stateCell = cell => (cell.data ? <Pill tone={cell.data.stateTone}>{cell.data.stateText}</Pill> : '')

const sessionCell = cell => {
  const session = cell.data
  if (!session) return ''
  // On a phone the state, Stalled when nothing has happened since the launch,
  // and how long ago it launched, then the learner (an admin looks for a
  // stalled session first).
  const line = session.phone
    ? (
      <PhoneLine
        pill={
          <React.Fragment>
            <Pill tone={session.stateTone}>{session.stateText}</Pill>
            {session.stalled && <Pill tone="warn" title="No activity since launch — likely abandoned">Stalled</Pill>}
          </React.Fragment>
        }
        title={`${agoOf(session.launchedAt)} · ${session.learnerText}`}>
        <AgoCell value={session.launchedAt} /> · {session.learnerText}
      </PhoneLine>
    )
    : (
      <span className="lms-phone-line sw-cell-sub">
        <Clip size="m">{session.learnerText}</Clip>
        <span className="sw-mono">{session.tokenText}</span>
      </span>
    )
  return <NameCell name={session.courseName} to={`/org/lms/sessions/${session.ref}`} line={line} />
}

// How long ago the session launched, with a Stalled pill when nothing has
// happened since.
const launchedCell = cell =>
  cell.data ? (
    <span className="d-inline-flex align-items-center gap-2">
      <AgoCell value={cell.data.launchedAt} />
      {cell.data.stalled && <Pill tone="warn" title="No activity since launch — likely abandoned">Stalled</Pill>}
    </span>
  ) : ''
